// 带扩展名，否则 `node --test` 解析不了这个模块（Vite 两种都认）。
import { API } from "../constants.js";

// ponytail: crypto.randomUUID 只在安全上下文（https / localhost）里存在，线上是明文 http 域名，
// 直接调会 "is not a function"。getRandomValues 没有这个限制，用它拼一个 v4。
export function randomId() {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export async function getMarketStatus() {
  return fetchJson("/api/market-data/status");
}

export async function getLatestMarketDate(assetCode) {
  const query = assetCode ? `?asset_code=${encodeURIComponent(assetCode)}` : "";
  return fetchJson(`/api/market-data/latest-date${query}`);
}

export async function getSession() {
  return fetchJson("/api/auth/session");
}

export async function login(username, password) {
  return fetchJson("/api/auth/login", {
    method: "POST",
    body: { username, password },
  });
}

// Lucimix iframe 入口：一次性票据交给后端换身份并建立会话（票据 60 秒有效、只能用一次）。
export async function verifyLucimixTicket(ticket) {
  return fetchJson("/api/auth/lucimix/verify", {
    method: "POST",
    body: { ticket },
  });
}

export async function logout() {
  return fetchJson("/api/auth/logout", { method: "POST" });
}

export function getAccountSyncBatch(batchId, accessToken) {
  // 批次权限依赖请求头，不参与仅按 URL 合并的普通 GET 请求池。
  return performJsonRequest(`/api/account-sync/batches/${encodeURIComponent(batchId)}`, DEFAULT_TIMEOUT_MS, {
    headers: { "X-Account-Sync-Token": accessToken },
  });
}

export function confirmAccountSync(batchId, confirmationToken, selectedIds, accessToken) {
  return fetchJson(`/api/account-sync/batches/${encodeURIComponent(batchId)}/confirm`, {
    method: "POST", headers: { "X-Account-Sync-Token": accessToken },
    body: { confirmation_token: confirmationToken, selected_candidate_ids: selectedIds },
  });
}

export function pairAccountSyncTransactions(batchId, pair, accessToken) {
  return fetchJson(`/api/account-sync/batches/${encodeURIComponent(batchId)}/pair`, {
    method: "POST", headers: { "X-Account-Sync-Token": accessToken }, body: pair,
  });
}

// 订阅（v08）：状态；充值后点「重新检查」立即补扣（后端 30 秒内连点只打一次 janus）。
export async function getSubscriptionStatus() {
  return fetchJson("/api/subscription/status");
}

export async function retrySubscription() {
  return fetchJson("/api/subscription/retry", { method: "POST" });
}

export async function getPortfolios() {
  return fetchJson("/api/portfolios");
}

export async function getQualityGate(portfolioId) {
  return fetchJson(`/api/portfolios/${portfolioId}/quality-gate`);
}

export async function getLatestSnapshot(portfolioId) {
  return fetchJson(`/api/portfolios/${portfolioId}/snapshot/latest`);
}

export async function getPortfolioCompare(portfolioIds, asOfDate = null) {
  return fetchJson("/api/portfolio-compare", {
    method: "POST",
    body: { portfolio_ids: portfolioIds, as_of_date: asOfDate },
  });
}

export async function getPortfolioDataAvailability(portfolioId, valuationDate) {
  const query = `?valuation_date=${encodeURIComponent(valuationDate)}`;
  return fetchJson(`/api/portfolios/${portfolioId}/data-availability${query}`);
}

// V0.4 AC-006/007/008：组合合集——组合之上的持久合并汇总对象，和"多组合对比"
// (getPortfolioCompare，临时横向并列) 是两个不同的产品概念，不要混用。
export async function getPortfolioCollections() {
  return fetchJson("/api/portfolio-collections");
}

export async function getPortfolioCollection(collectionId) {
  return fetchJson(`/api/portfolio-collections/${collectionId}`);
}

export async function createPortfolioCollection(name, portfolioIds) {
  return fetchJson("/api/portfolio-collections", {
    method: "POST",
    body: { name, portfolio_ids: portfolioIds },
  });
}

export async function updatePortfolioCollection(collectionId, payload) {
  return fetchJson(`/api/portfolio-collections/${collectionId}`, {
    method: "PATCH",
    body: payload,
  });
}

export async function deletePortfolioCollection(collectionId) {
  return fetchJson(`/api/portfolio-collections/${collectionId}`, { method: "DELETE" });
}

// ADR-010 全局查看镜头：合集视图实时聚合（只读，不落库）。valuationDate 为空 =
// 默认态（聚合成员 latest 快照）；非空 = 镜头激活（逐成员按该日期现算，就近取值）。
// 原 summarize / snapshot/latest 两个端点已废弃。
export async function getPortfolioCollectionView(
  collectionId, valuationDate = null, includeAssetAttribution = false,
) {
  const params = new URLSearchParams();
  if (valuationDate) params.set("valuation_date", valuationDate);
  if (includeAssetAttribution) params.set("include_asset_attribution", "true");
  const query = params.size ? `?${params.toString()}` : "";
  return fetchJson(`/api/portfolio-collections/${collectionId}/view${query}`);
}

export async function getMarketSnapshot() {
  return fetchJson("/api/market/snapshot/current");
}

export async function getMarketRealtimeStatus() {
  return fetchJson("/api/market/realtime/status");
}

export async function getLiveInstrumentIntraday({ code, kind, isIndex = false, previousClose = null }) {
  const path = kind === "industry"
    ? `/api/market/industries/${encodeURIComponent(code)}/live-intraday`
    : `/api/market/instruments/${encodeURIComponent(code)}/live-intraday`;
  const raw = await fetchJson(path);
  // An industry has no live official index feed -- the backend reconstructs
  // pct_chg from an equal-weight constituent recompute. When the caller
  // supplies the industry's own EOD close, convert that into an absolute
  // index-level line (same shape as a stock/index price series) so it gets
  // the same 昨收 reference-line/twin-axis chart treatment; otherwise keep
  // returning the plain percent series callers already handle.
  const convertIndustryToLevel = kind === "industry" && Number.isFinite(previousClose) && previousClose !== 0;
  const points = kind === "industry"
    ? raw.data.points.map((point) => ({
        t: point.t,
        raw: convertIndustryToLevel ? previousClose * (1 + Number(point.pct_chg) / 100) : Number(point.pct_chg),
      }))
    : raw.data.points.map((point) => ({ t: point.t, raw: Number(point.price), volume: point.volume }));
  return {
    meta: {
      trade_date: raw.data.trade_date ?? null,
      fetched_at: raw.meta?.fetched_at ?? null,
      read_path: raw.meta?.read_path ?? null,
      source_id: raw.data.source_id ?? null,
      series_status: raw.data.series_status ?? null,
      series_reason: raw.data.series_reason ?? null,
      point_count: raw.data.point_count ?? raw.data.points.length,
    },
    data: {
      quote: raw.data.quote
        ? {
            ...raw.data.quote,
            price: Number(raw.data.quote.price),
            pre_close: Number(raw.data.quote.pre_close),
            change: Number(raw.data.quote.change),
            pct_chg: Number(raw.data.quote.pct_chg),
            open: Number(raw.data.quote.open),
            high: Number(raw.data.quote.high),
            low: Number(raw.data.quote.low),
            volume: Number(raw.data.quote.volume),
            amount_cny: Number(raw.data.quote.amount_cny),
            turnover_rate: Number(raw.data.quote.turnover_rate),
          }
        : null,
      series: [
        {
          name: raw.data.name,
          unit: convertIndustryToLevel ? "index_point" : kind === "industry" ? "percent" : isIndex ? "index_point" : "CNY",
          method: kind === "industry" ? raw.data.method : "live_vendor_passthrough",
          points,
        },
      ],
    },
  };
}

export async function getLatestGlobalIndex({ code }) {
  const raw = await fetchJson(`/api/market/instruments/${encodeURIComponent(code)}/latest-close`);
  return {
    market_date: raw.data.market_date,
    close: Number(raw.data.close),
    pct_chg: Number(raw.data.pct_chg),
    // Present only when the quote is genuinely live (HK/Korea in-session, US
    // overnight); absent means the payload is the latest EOD close.
    as_of_ts: raw.data.as_of_ts ?? null,
  };
}

export async function getMarketRank({ snapshotId, ...filters } = {}) {
  const endpoint = "/api/market/rank";
  const params = new URLSearchParams();
  if (snapshotId) params.set("snapshot_id", snapshotId);
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") {
      params.set(key, String(value));
    }
  });
  return fetchJson(`${endpoint}?${params.toString()}`);
}

// 概览页只要指数卡片，不要 overview() 顺带算的行业活跃度/市场宽度/融资余额
// （实测整体 4.5s，其中指数部分只占 3.8ms）。A股指数由后端用腾讯实时价覆盖。
export async function getMarketIndexCards() {
  const raw = await fetchJson("/api/market/index-cards");
  return raw.data;
}

export async function getMarketOverview(asOfDate = null, snapshotId = null) {
  if (!snapshotId) {
    const query = asOfDate ? `?as_of_date=${encodeURIComponent(asOfDate)}` : "";
    return fetchJson(`/api/market/overview${query}`);
  }
  const raw = await fetchJson(`/api/market/overview?snapshot_id=${encodeURIComponent(snapshotId)}`);
  const data = raw.data;
  const turnover = data.turnover ?? {};
  const financing = data.financing ?? {};
  const connect = (data.connect?.segments ?? [])
    .flatMap((segment) => (segment.series ?? []).map((item) => ({
      ...item,
      regime: segment.regime,
    })))
    .sort((left, right) => left.date.localeCompare(right.date));
  const latestConnect = connect.at(-1) ?? null;
  const latestConnectRegime = latestConnect?.regime ?? null;
  const connectValueKey = latestConnectRegime === "current_turnover"
    ? "target_compatible_amount_yi"
    : "northbound_net";
  const connectSeries = connect
    .filter((item) => item.regime === latestConnectRegime && item[connectValueKey] !== undefined)
    .map((item) => ({
      date: item.date,
      amount_yi: latestConnectRegime === "current_turnover"
        ? Number(item[connectValueKey])
        : toYi(item[connectValueKey]),
    }))
    // Match the reference site's window for this card; the full history is
    // still available to other consumers of the connect_daily fact.
    .slice(-60);
  return {
    status: "ok",
    data_date: raw.meta.business_date,
    source: "market_observation snapshot",
    evidence_refs: raw.meta.evidence_refs,
    domestic_indices: (data.domestic_indices ?? []).map(normalizeIndex),
    global_indices: (data.global_indices ?? []).map(normalizeIndex),
    breadth: normalizeBreadth(data.breadth),
    turnover: {
      data_date: raw.meta.business_date,
      amount_yi: toYi(turnover.amount),
      change_pct: turnover.change_pct,
      ma20_yi: toYi(turnover.ma20),
      distribution: normalizeDistribution(data.breadth?.distribution),
      series: (turnover.series_60d ?? []).map((item) => ({
        date: item.date,
        amount_yi: toYi(item.amount),
        ma20_yi: toYi(item.ma20),
      })),
    },
    margin: {
      data_date: financing.data_date,
      balance_yi: toYi(financing.balance),
      change_yi: toYi(financing.change_1d),
      change_5d_yi: toYi(financing.change_5d),
      series: (financing.series ?? []).map((item) => ({ date: item.date, balance_yi: toYi(item.balance) })),
    },
    connect: latestConnect ? {
      data_date: latestConnect.date,
      regime: latestConnectRegime,
      display_method: data.connect?.display_method ?? null,
      display_formula: data.connect?.display_formula ?? null,
      label: "北向资金",
      series_label: latestConnectRegime === "current_turnover" ? "北向资金（兼容口径）" : "北向净额",
      amount_yi: connectSeries.at(-1)?.amount_yi ?? null,
      change_1d_yi: connectSeries.length >= 2
        ? connectSeries.at(-1).amount_yi - connectSeries.at(-2).amount_yi
        : null,
      sum_5d_yi: connectSeries.length
        ? connectSeries.slice(-5).reduce((sum, item) => sum + (item.amount_yi ?? 0), 0)
        : null,
      series: connectSeries,
    } : null,
    huijin_etf: data.huijin_etf ?? null,
  };
}

export async function getMarketIndustries({ query = "", sort = "pct_chg", order = "desc", asOfDate = null, snapshotId = null, level = "all" } = {}) {
  if (!snapshotId) {
    const params = new URLSearchParams({ query, sort, order });
    if (asOfDate) params.set("as_of_date", asOfDate);
    return fetchJson(`/api/market/industries?${params.toString()}`);
  }
  const v05Sort = sort === "pct_chg" ? "equal_weight_pct_chg" : sort;
  const params = new URLSearchParams({ snapshot_id: snapshotId, level, query, sort: v05Sort, order });
  const raw = await fetchJson(`/api/market/industries?${params.toString()}`);
  return normalizeIndustries(raw);
}

export async function getMarketIndustryFlow({ level = "L1", query = "", sort = "main_net_inflow_yi", order = "desc", asOfDate = null, snapshotId = null } = {}) {
  if (!snapshotId) {
    const params = new URLSearchParams({ level, query, sort, order });
    if (asOfDate) params.set("as_of_date", asOfDate);
    return fetchJson(`/api/market/industry-flow?${params.toString()}`);
  }
  const v05Sort = sort === "main_net_inflow_yi" ? "main_net_inflow" : sort;
  const params = new URLSearchParams({ snapshot_id: snapshotId, level, query, sort: v05Sort, order });
  const raw = await fetchJson(`/api/market/industry-flow?${params.toString()}`);
  return {
    status: "ok",
    data_date: raw.data.fact_date ?? raw.meta.business_date,
    page_date: raw.data.page_date ?? raw.meta.business_date,
    disclosure_lag: raw.data.disclosure_lag,
    evidence_refs: raw.meta.evidence_refs,
    items: (raw.data.items ?? []).map((item) => ({
      ...normalizeIndustry(item),
      main_net_inflow_yi: toYi(item.main_net_inflow),
      moneyflow_fact_date: item.fact_date ?? raw.data.fact_date ?? null,
    })),
  };
}

export async function getMarketIndustryTrends({ snapshotId, level = "L1", metric = "market_turnover_share", rangeDays = 120, selected = [], query = "" } = {}) {
  const params = new URLSearchParams({ snapshot_id: snapshotId, level, metric, range_days: String(rangeDays), selected: selected.join(","), query });
  return fetchJson(`/api/market/industry-trends?${params.toString()}`);
}

export async function getMarketInstrumentSearch({ snapshotId, query, types = ["sw_l1", "sw_l2", "stock"], limit = 20 } = {}) {
  const params = new URLSearchParams({ snapshot_id: snapshotId, q: query, types: types.join(","), limit: String(limit) });
  return fetchJson(`/api/market/instruments/search?${params.toString()}`);
}

// 概览「添加关注 / 新增持仓」的搜索框。**不带 snapshot_id**：它查的是行情库本身，
// 不是市场观察的快照——快照没发布不该让人搜不到自己的标的。
export async function getMarketInstrumentLookup({ query, limit = 20 } = {}) {
  const params = new URLSearchParams({ q: query, limit: String(limit) });
  return fetchJson(`/api/market/instruments/lookup?${params.toString()}`);
}

export async function getMarketInstrumentProfile({ snapshotId, code } = {}) {
  const params = new URLSearchParams();
  if (snapshotId) params.set("snapshot_id", snapshotId);
  const query = params.size ? `?${params.toString()}` : "";
  return fetchJson(`/api/market/instruments/${encodeURIComponent(code)}/profile${query}`);
}

export async function getStockDetail({ snapshotId, code, compact = false } = {}) {
  const params = new URLSearchParams();
  if (snapshotId) params.set("snapshot_id", snapshotId);
  if (compact) params.set("compact", "true");
  return fetchJson(`/api/market/instruments/${encodeURIComponent(code)}/detail?${params.toString()}`);
}

export async function getStockKline({ snapshotId, code, period = "daily", count = 240, startDate = null } = {}) {
  const params = new URLSearchParams({ period, count: String(count) });
  if (snapshotId) params.set("snapshot_id", snapshotId);
  if (startDate) params.set("start_date", startDate);
  return fetchJson(`/api/market/instruments/${encodeURIComponent(code)}/kline?${params.toString()}`);
}

export async function getMarketSeries({ snapshotId, instrumentIds, period = "daily", tradeDate = null, rangeDays = 120, normalization = "none" } = {}) {
  const params = new URLSearchParams({ snapshot_id: snapshotId, instrument_ids: instrumentIds.join(","), period, range_days: String(rangeDays), normalization });
  if (tradeDate) params.set("trade_date", tradeDate);
  return fetchJson(`/api/market/series?${params.toString()}`);
}

function normalizeIndex(item) {
  return { ...item, trade_date: item.market_date };
}

function normalizeBreadth(breadth = {}) {
  return {
    rise: breadth.up,
    flat: breadth.flat,
    fall: breadth.down,
    limit_up: breadth.limit_up,
    limit_down: breadth.limit_down,
    total: breadth.eligible,
  };
}

function normalizeDistribution(distribution = []) {
  const counts = new Map(distribution.map((item) => [item.band, item.count]));
  return {
    ge_10: counts.get("gte_10") ?? 0,
    p7_10: counts.get("7_to_10") ?? 0,
    p3_7: counts.get("3_to_7") ?? 0,
    p0_3: counts.get("0_to_3") ?? 0,
    flat: counts.get("flat") ?? 0,
    n0_3: counts.get("neg_0_to_3") ?? 0,
    n3_7: counts.get("neg_3_to_7") ?? 0,
    n7_10: counts.get("neg_7_to_10") ?? 0,
    le_n10: counts.get("lte_neg_10") ?? 0,
  };
}

function normalizeIndustries(raw) {
  const items = (raw.data.items ?? []).map(normalizeIndustry);
  const l1Items = items.filter((item) => item.industry_level === "L1");
  const l2Items = items.filter((item) => item.industry_level === "L2");
  let normalizedItems = items;
  if (l1Items.length && l2Items.length) {
    const childrenByParent = new Map();
    for (const item of l2Items) {
      const children = childrenByParent.get(item.parent_industry_id) ?? [];
      children.push(item);
      childrenByParent.set(item.parent_industry_id, children);
    }
    const visibleParentIds = new Set(l1Items.map((item) => item.industry_id));
    const orphanedMatches = l2Items.filter((item) => !visibleParentIds.has(item.parent_industry_id));
    normalizedItems = [
      ...l1Items.map((item) => ({
        ...item,
        children: childrenByParent.get(item.industry_id) ?? [],
      })),
      ...orphanedMatches,
    ];
  }
  return {
    status: "ok",
    data_date: raw.data.fact_date ?? raw.meta.business_date,
    snapshot_date: raw.data.snapshot_date ?? raw.meta.business_date,
    quote_mode: raw.data.quote_mode ?? "published_eod",
    source_as_of_ts: raw.data.source_as_of_ts ?? null,
    evidence_refs: raw.meta.evidence_refs,
    items: normalizedItems,
    total_l1: l1Items.length,
  };
}

function normalizeIndustry(item) {
  return {
    ...item,
    industry_code: item.code,
    industry_name: item.name,
    industry_level: item.level,
    pct_chg: item.equal_weight_pct_chg,
    pct_chg_5d: item.equal_weight_pct_chg_5d,
    rise: item.up,
    fall: item.down,
    turnover_yi: toYi(item.turnover_amount),
  };
}

function toYi(value) {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed / 100000000 : null;
}

export async function getMarketIndustryActivity({
  level = "L1", rangeDays = 60, selected = [], query = "", asOfDate = null,
} = {}) {
  const params = new URLSearchParams({
    level,
    range_days: String(rangeDays),
    selected: selected.join(","),
    query,
  });
  if (asOfDate) params.set("as_of_date", asOfDate);
  return fetchJson(`/api/market/industry-activity?${params.toString()}`);
}

export async function getMarketIndustryDetail(industryCode, { period = "daily", asOfDate = null, snapshotId = null } = {}) {
  if (snapshotId) {
    const params = new URLSearchParams({ snapshot_id: snapshotId, page_size: "500" });
    const detail = await fetchJson(`/api/market/industries/${encodeURIComponent(industryCode)}?${params.toString()}`);
    const industry = detail.data.industry;
    const derivedIndex = industry.index_data_status === "derived"
      || industry.index_bar_status === "close_only";
    return {
      status: derivedIndex || detail.meta.completeness === "provisional" ? "provisional" : "ok",
      // 与汇总页(getMarketIndustries)一致：有盘中实时批次就用其交易日
      // （详情端点把它放在 data.industry.breadth_fact_date），否则用快照业务日。
      // 否则汇总页显示实时日、详情页显示快照日，两页日期打架。
      data_date: detail.data.industry?.breadth_fact_date ?? detail.meta.business_date,
      evidence_refs: detail.meta.evidence_refs,
      industry: {
        industry_id: industry.industry_id,
        industry_code: industry.code,
        industry_name: industry.name,
        industry_level: industry.level,
        index_close: industry.index_close,
        pct_chg: industry.index_pct_chg,
        pct_chg_5d: industry.index_pct_chg_5d,
        rise: industry.up,
        flat: industry.flat,
        fall: industry.down,
        turnover_yi: toYi(industry.turnover_amount),
        series_method: industry.index_method === "official_sw_index"
          ? "official_index_daily"
          : (industry.index_method || "derived_constituent_weighted"),
        series_source_id: industry.index_source_id,
        index_market_date: industry.index_market_date,
        index_data_status: industry.index_data_status,
        index_bar_status: industry.index_bar_status,
        index_bar_method: industry.index_bar_method,
      },
      series_status: "ok",
      member_coverage: {
        priced_members: detail.data.members.total,
        total_members: industry.member_count,
        missing_price_members: 0,
      },
      members: (detail.data.members.items ?? []).map((item) => ({
        ...item,
        price: item.close,
        l2_industry: item.l2_industry_name,
        l2_industry_code: item.l2_industry_code,
        total_mv_yi: toYi(item.total_mv),
        main_net_inflow_yi: null,
        status: "ok",
      })),
      source: industry.index_source_id,
      snapshot_completeness: detail.meta.completeness,
    };
  }
  const params = new URLSearchParams({ period });
  if (asOfDate) params.set("as_of_date", asOfDate);
  return fetchJson(`/api/market/industries/${encodeURIComponent(industryCode)}?${params.toString()}`);
}

// ADR-010：单组合详情页的默认数据源（只读现算，不落库）。valuationDate 为空
// 时按最新数据现算（2026-07-10 起单组合详情默认态也走这个端点，不再要求
// 用户先点"重新核算"才能看到最新数字）；镜头激活时传具体历史日期。
export async function getPortfolioView(
  portfolioId, valuationDate = null, includeAssetAttribution = false,
) {
  const params = new URLSearchParams();
  if (valuationDate) params.set("valuation_date", valuationDate);
  if (includeAssetAttribution) params.set("include_asset_attribution", "true");
  const query = params.size ? `?${params.toString()}` : "";
  return fetchJson(`/api/portfolios/${portfolioId}/view${query}`);
}

export async function savePortfolioView(payload) {
  return fetchJson("/api/portfolio-views", {
    method: "POST",
    body: payload,
  });
}

export async function calculatePortfolio(portfolioId, payload) {
  return fetchJson(`/api/portfolios/${portfolioId}/calculate`, {
    method: "POST",
    body: payload,
  });
}

export async function parseSingleAsset(input) {
  return fetchJson("/api/single-assets/parse", {
    method: "POST",
    body: { input },
  });
}

export async function calculateSingleAssetPosition(payload) {
  return fetchJson("/api/single-assets/calculate", {
    method: "POST",
    body: payload,
  });
}

export async function askAgentTool(message, pageContext, history = []) {
  return fetchJson("/api/agent/chat", {
    method: "POST",
    body: { message, page_context: pageContext, history },
  });
}

export async function renamePortfolio(portfolioId, name) {
  return fetchJson(`/api/portfolios/${portfolioId}`, { method: "PATCH", body: { name } });
}

export async function deletePortfolio(portfolioId) {
  return fetchJson(`/api/portfolios/${portfolioId}`, { method: "DELETE" });
}

export async function addTrade(portfolioId, trade) {
  return fetchJson(`/api/portfolios/${portfolioId}/trades`, { method: "POST", body: trade });
}

export async function updateTrade(portfolioId, tradeId, trade) {
  return fetchJson(`/api/portfolios/${portfolioId}/trades/${tradeId}`, { method: "PATCH", body: trade });
}

export async function deleteTrade(portfolioId, tradeId) {
  return fetchJson(`/api/portfolios/${portfolioId}/trades/${tradeId}`, { method: "DELETE" });
}

export async function confirmInferredDeposit(portfolioId, tradeId, amount = null, tradeDate = null) {
  return fetchJson(`/api/portfolios/${portfolioId}/inferred-deposits/${tradeId}/confirm`, {
    method: "POST",
    body: { amount, trade_date: tradeDate },
  });
}

export async function parseImportText(text) {
  return fetchJson("/api/imports/parse", {
    method: "POST",
    body: { text },
  });
}

export async function parseSingleAssetDraft(message, rows = [], history = []) {
  return fetchJson("/api/single-asset-drafts/parse", {
    method: "POST",
    body: { message, rows, history },
  });
}

export async function createSingleAssetAgentConversation() {
  return fetchJson("/api/single-asset-agent/conversations", {
    method: "POST",
  });
}

export async function listSingleAssetAgentConversations({ limit = 50, offset = 0 } = {}) {
  const query = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  return fetchJson(`/api/single-asset-agent/conversations?${query.toString()}`);
}

export async function getSingleAssetAgentConversation(conversationId, { historyLimit = 8 } = {}) {
  const query = new URLSearchParams({ history_limit: String(historyLimit) });
  return fetchJson(`/api/single-asset-agent/conversations/${encodeURIComponent(conversationId)}?${query.toString()}`);
}

export async function bindSingleAssetAgentView(
  conversationId,
  expectedConversationRevision,
  view,
  { reuseExisting = true, idempotencyKey = randomId() } = {},
) {
  return fetchJson(`/api/single-asset-agent/conversations/${encodeURIComponent(conversationId)}/bindings`, {
    method: "POST",
    body: {
      expected_conversation_revision: expectedConversationRevision,
      view,
      reuse_existing: reuseExisting,
      idempotency_key: idempotencyKey,
    },
  });
}

export async function getSingleAssetBindingView(conversationId, bindingId) {
  return fetchJson(
    `/api/single-asset-agent/conversations/${encodeURIComponent(conversationId)}/bindings/${encodeURIComponent(bindingId)}/view`,
  );
}

// 「记录交易」弹窗整本账提交：transactions 是提交后的完整交易序列——
// 带 transaction_id 的是历史行，不带的是新增，没回传的旧 id 即删除。
// openingPositions 是录入持仓的完整批次列表，按 batch_id 同样语义（2026-09-16）。
// expectedTargetRevision 必须是打开弹窗时读到的那个，提交时不许重读（spec §3.3）。
// 契约见 docs/sdd/astock-agent-v07-single-asset-agent-behavior/structured-intake-contract.md。
export async function submitBindingLedger(
  conversationId,
  bindingId,
  {
    assetCode,
    openingPositions,
    transactions,
    expectedTargetRevision,
    idempotencyKey = randomId(),
  },
) {
  return fetchJson(
    `/api/single-asset-agent/conversations/${encodeURIComponent(conversationId)}/bindings/${encodeURIComponent(bindingId)}/ledger`,
    {
      method: "POST",
      body: {
        asset_code: assetCode,
        opening_positions: openingPositions,
        transactions,
        expected_target_revision: expectedTargetRevision,
        idempotency_key: idempotencyKey,
      },
    },
  );
}

export async function convertCalculationWorkspaceToHolding(
  workspaceId,
  { expectedRevision, conversationId, bindingId, idempotencyKey = randomId() },
) {
  return fetchJson(`/api/calculation-workspaces/${encodeURIComponent(workspaceId)}/convert-to-holding`, {
    method: "POST",
    body: {
      expected_revision: expectedRevision,
      conversation_id: conversationId,
      binding_id: bindingId,
      idempotency_key: idempotencyKey,
    },
  });
}

export async function getSingleAssetAgentMarketContext(code) {
  return fetchJson(
    `/api/single-asset-agent/market-context/${encodeURIComponent(code)}`,
  );
}

export async function getSingleAssetMarketView(asset, period = "60d") {
  const query = new URLSearchParams({ asset, period });
  return fetchJson(`/api/single-asset-agent/market-view?${query.toString()}`);
}

export async function getOpenFundTradeNav(assetCode, tradeDate) {
  const query = new URLSearchParams({
    asset_code: assetCode,
    trade_date: tradeDate,
  });
  return fetchJson(`/api/single-asset-agent/open-fund-nav?${query.toString()}`);
}

export async function searchSingleAssetComparisons({ query, types, limit = 10 }) {
  const params = new URLSearchParams({
    q: query,
    types: types.join(","),
    limit: String(limit),
  });
  return fetchJson(`/api/single-asset-agent/comparisons/search?${params.toString()}`);
}

export async function getSingleAssetComparisonSeries({ instrumentIds, endDate }) {
  const params = new URLSearchParams({ instrument_ids: instrumentIds.join(",") });
  if (endDate) params.set("end_date", endDate);
  return fetchJson(`/api/single-asset-agent/comparisons/series?${params.toString()}`);
}

// 把一句话解析成待核对的事实。**这个端点没有写库的能力**——落库一律走
// submitBindingLedger，用户打字和手点「记录交易」
// 因此停在同一个弹窗、同一个提交动作上。
// conversation_id 与 expected_revision 都是必填：服务端会把这一问一答写进会话历史，
// revision 跟着 +1，返回值里的 revision 才是下一次该带的。
export async function parseSingleAssetMessage(
  message,
  { conversationId, expectedRevision, bindingId = null, targetAssetCode = null },
) {
  return fetchJson("/api/single-asset-agent/parse", {
    method: "POST",
    timeoutMs: AGENT_TURN_TIMEOUT_MS,
    body: {
      message,
      conversation_id: conversationId,
      expected_revision: expectedRevision,
      binding_id: bindingId,
      target_asset_code: targetAssetCode,
    },
  });
}

// 纯问答（「我持仓多少」「这只票怎么样」）仍然走 /turn——/parse 解析不出事实时
// 只会回一句"没解析出来"。2026-09-03 起录入不再走这里，暂时没有前端调用点；
// 要接回问答就调它，不要给 /parse 加问答分支。
export async function runSingleAssetAgentTurn(
  message,
  conversationId = null,
  expectedRevision = null,
  targetAssetCode = null,
  target = null,
  idempotencyKey = null,
) {
  const body = {
    message,
    conversation_id: conversationId,
    expected_revision: expectedRevision,
    target_asset_code: targetAssetCode,
    target,
    idempotency_key: idempotencyKey,
  };
  const result = await fetchJson("/api/single-asset-agent/turn", {
    method: "POST",
    timeoutMs: AGENT_TURN_TIMEOUT_MS,
    body,
  });
  // 走查要记的第 1 层（请求 payload）只有这里知道全貌。开发环境把它挂回
  // 这一轮的响应上——必须同体挂载，不能存成模块级的"最近一次请求"：
  // 请求在发出时更新、响应在返回时更新，中间那段窗口会把第 N 轮的请求
  // 配上第 N-1 轮的响应，诊断块就会说谎。
  // 见 docs/testing/walkthrough-record-template.md。
  if (import.meta.env?.DEV && result && typeof result === "object") {
    result.__devRequest = body;
    lastAgentTurn = result;
  }
  return result;
}

// 走查诊断块要记的是**这一轮 /turn 的响应**，而组件里的 turnResult 会被覆写：
// 换页后 /single 是全新挂载，它用 GET /conversations/{id} 恢复状态，
// 而那个接口在 new_read_new_write 下恒空（§10-I），于是 turnResult 变成
// rows:0 / status:empty，诊断块六层里有三层会记成空表。
// 这里留的是响应对象本身（请求已同体挂在 __devRequest 上），不受下游 setState 影响。
let lastAgentTurn = null;

export function readLastAgentTurn() {
  return lastAgentTurn;
}

export async function extractSingleAssetAgentImportText(file) {
  const form = new FormData();
  form.append("file", file);
  const response = await fetch(`${API}/api/single-asset-agent/import-text`, {
    method: "POST",
    body: form,
    credentials: "include",
  });
  const payload = await response.json();
  if (!response.ok) {
    const error = new Error(payload.error?.message ?? `HTTP ${response.status}`);
    error.payload = payload;
    error.status = response.status;
    throw error;
  }
  return payload;
}

// 概览页挂载时这个端点会被请求两次：主页面要整张表，内嵌的 Agent 面板只要判断
// 当前标的在不在追踪里。服务端一次要 ~350ms，且是纯 Python 计算（实测两线程并发
// 0.94x，GIL 卡死，并发省不到时间），所以第二次是实打实多花的 350ms。
//
// 只合并「同时在飞」的请求，不缓存已完成的结果——请求一 settle 就释放，
// 加/删关注后的重新拉取照常生效，不存在读到旧数据的窗口。
let trackingOverviewInFlight = null;

export async function getAssetTrackingOverview() {
  if (trackingOverviewInFlight) return trackingOverviewInFlight;
  trackingOverviewInFlight = fetchJson("/api/asset-tracking/overview").finally(() => {
    trackingOverviewInFlight = null;
  });
  return trackingOverviewInFlight;
}

export async function getSingleAssetAgentMarketQuote(code) {
  return fetchJson(`/api/single-asset-agent/market-quote/${encodeURIComponent(code)}`);
}

export async function getSingleAssetAgentMarketNews(code) {
  return fetchJson(`/api/single-asset-agent/market-news/${encodeURIComponent(code)}`);
}

export async function getAssetTrackingStatus(assetCode) {
  return fetchJson(`/api/asset-tracking/assets/${encodeURIComponent(assetCode)}/status`);
}

export async function getAssetTrackingMarketPulse() {
  return fetchJson("/api/asset-tracking/market-pulse");
}

export async function untrackOverviewHolding(assetCode) {
  return fetchJson(`/api/asset-tracking/holdings/${encodeURIComponent(assetCode)}`, {
    method: "DELETE",
  });
}

export async function addAssetWatch(asset) {
  return fetchJson(`/api/asset-tracking/watches/${encodeURIComponent(asset)}`, {
    method: "POST",
  });
}

export async function removeAssetWatch(assetCode) {
  return fetchJson(`/api/asset-tracking/watches/${encodeURIComponent(assetCode)}`, {
    method: "DELETE",
  });
}

export async function replaceAssetTags(assetCode, labels) {
  return fetchJson(`/api/asset-tracking/assets/${encodeURIComponent(assetCode)}/tags`, {
    method: "PUT",
    body: { labels },
  });
}

export async function getAssetTags(assetCode) {
  return fetchJson(`/api/asset-tracking/assets/${encodeURIComponent(assetCode)}/tags`);
}

// 标签汇总页：成员 = 当前带该标签的持仓与关注（契约见 06-api-contracts.md 2026-09-30 增量）。
export async function getTagSummary(tag) {
  return fetchJson(`/api/asset-tracking/tags/${encodeURIComponent(tag)}/summary`);
}

// 自然语言查找（添加关注页）。`/nl` 是唯一调 LLM 的入口（约 2～8 秒，超时放宽到 60 秒）；
// 翻页 / 换排序 / 去掉某个条件都走 `/run`，不再调 LLM。
export async function screenerNl(query) {
  return fetchJson("/api/screener/nl", { method: "POST", body: { query }, timeoutMs: 60000 });
}

// 对一次查找的反馈（good / bad / edit），用来复盘翻译对不对。
export async function screenerFeedback(body) {
  return fetchJson("/api/screener/feedback", { method: "POST", body });
}

export async function screenerRun(body) {
  return fetchJson("/api/screener/run", { method: "POST", body });
}

// XLSX 在后端读取工作表后，复用与文本相同的 LLM 解析及 review / P0 门禁；上传不写账本。
export async function uploadImportXlsx(file) {
  const form = new FormData();
  form.append("file", file);
  const response = await fetch(`${API}/api/imports/xlsx`, {
    method: "POST",
    body: form,
    credentials: "include",
  });
  const payload = await response.json();
  if (!response.ok) {
    const error = new Error(payload.error?.message ?? `HTTP ${response.status}`);
    error.payload = payload;
    error.status = response.status;
    throw error;
  }
  return payload;
}

export async function reviewPortfolioImport(portfolioId, rows) {
  if (portfolioId) {
    return fetchJson(`/api/portfolios/${portfolioId}/imports/parse`, { method: "POST", body: { rows } });
  }
  // V0.4 AC-013：/api/imports/review 现在按"投资组合"列分组返回 groups[]。
  // 批次导航 UI（T075/T076）还没做，这里先解出第一组，保持现有单组合导入
  // 页面的消费点（main.jsx/importReviewTable.jsx/widgets.jsx/workspace.jsx）
  // 不用改——它们都读 row_analysis/summary 这些字段，group[0] 里都有。
  const result = await fetchJson("/api/imports/review", { method: "POST", body: { rows } });
  return { ...result.groups[0], groups: result.groups };
}

// V0.4 AC-004A：组合首次录入与追加交易共用入口，取代此前"先 POST /api/portfolios
// 创建组合再 POST .../imports/confirm"两步调用——后端在一个事务里原子创建组合
// 并写入交易，避免确认失败时遗留空组合。payload.portfolio_id 为空表示新建组合。
export async function confirmPortfolioLedger(payload) {
  return fetchJson("/api/portfolios/ledger/confirm", {
    method: "POST",
    body: payload,
  });
}

// V0.4 AC-014C：多组合批量导入原子确认——items 里任一组未通过校验，整批不写入
// 任何数据（后端 confirm_ledger_batch 的语义），不做"部分组合成功"。
export async function confirmLedgerBatch(payload) {
  return fetchJson("/api/portfolios/ledger/confirm-batch", {
    method: "POST",
    body: payload,
  });
}

export async function reviewSingleAssetBatchImport(rows) {
  return fetchJson("/api/single-asset-imports/review", {
    method: "POST",
    body: { rows },
  });
}

export async function confirmSingleAssetBatchImport(items) {
  return fetchJson("/api/single-asset-imports/confirm-batch", {
    method: "POST",
    body: { items },
  });
}

// V0.4 AC-009/010/010A：单标的长期账本——和 calculateSingleAssetPosition
// （一次性单笔买入核算，calculation.py）是两个不同的产品概念，不要混用。
export async function createSingleAssetCase(name, assetCode, rows) {
  return fetchJson("/api/single-asset-cases", {
    method: "POST",
    body: { name, asset_code: assetCode, rows },
  });
}

export async function getSingleAssetCases() {
  return fetchJson("/api/single-asset-cases");
}

export async function getSingleAssetCase(caseId) {
  return fetchJson(`/api/single-asset-cases/${caseId}`);
}

export async function renameSingleAssetCase(caseId, name) {
  return fetchJson(`/api/single-asset-cases/${caseId}`, { method: "PATCH", body: { name } });
}

export async function deleteSingleAssetCase(caseId) {
  return fetchJson(`/api/single-asset-cases/${caseId}`, { method: "DELETE" });
}

// 单标的历史查看与组合驾驶舱的查看镜头同义：只读现算，绝不写入快照。
export async function getSingleAssetCaseView(caseId, valuationDate) {
  const query = `?valuation_date=${encodeURIComponent(valuationDate)}`;
  return fetchJson(`/api/single-asset-cases/${caseId}/view${query}`);
}

export async function appendSingleAssetTrades(caseId, rows) {
  return fetchJson(`/api/single-asset-cases/${caseId}/trades`, {
    method: "POST",
    body: { rows },
  });
}

export async function validateNewSingleAssetTrades(assetCode, rows) {
  return fetchJson("/api/single-asset-cases/validate", {
    method: "POST",
    body: { asset_code: assetCode, rows },
  });
}

export async function validateSingleAssetTrades(caseId, rows) {
  return fetchJson(`/api/single-asset-cases/${caseId}/trades/validate`, {
    method: "POST",
    body: { rows },
  });
}

export async function updateSingleAssetTrade(caseId, tradeId, payload) {
  return fetchJson(`/api/single-asset-cases/${caseId}/trades/${tradeId}`, {
    method: "PATCH",
    body: payload,
  });
}

export async function deleteSingleAssetTrade(caseId, tradeId, reason) {
  return fetchJson(`/api/single-asset-cases/${caseId}/trades/${tradeId}`, {
    method: "DELETE",
    body: { reason },
  });
}

export async function calculateSingleAssetCase(caseId, valuationDate = null) {
  return fetchJson(`/api/single-asset-cases/${caseId}/calculate`, {
    method: "POST",
    body: { valuation_date: valuationDate },
  });
}

export async function confirmSingleAssetEvent(caseId, payload) {
  return fetchJson(`/api/single-asset-cases/${caseId}/event-confirmations`, {
    method: "POST",
    body: payload,
  });
}

// V0.4 AC-011/AC-012：导出 xlsx。三个导出端点返回文件流而非 JSON，走独立的
// downloadFile()，不能复用 fetchJson（它固定 response.json()）。
export async function exportPortfolio(portfolioId, valuationDate) {
  return downloadFile(`/api/portfolios/${portfolioId}/export?valuation_date=${encodeURIComponent(valuationDate)}`);
}

export async function exportPortfolioCollection(collectionId, valuationDate) {
  return downloadFile(`/api/portfolio-collections/${collectionId}/export?valuation_date=${encodeURIComponent(valuationDate)}`);
}

export async function exportSingleAssetCase(caseId, valuationDate) {
  return downloadFile(`/api/single-asset-cases/${caseId}/export?valuation_date=${encodeURIComponent(valuationDate)}`);
}

async function downloadFile(path) {
  const response = await fetch(`${API}${path}`, { credentials: "include" });
  if (!response.ok) {
    const payload = await response.json();
    notifyAuthenticationRequired(path, response, payload);
    const error = new Error(payload.error?.message ?? `HTTP ${response.status}`);
    error.payload = payload;
    error.status = response.status;
    throw error;
  }
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/);
  const asciiMatch = disposition.match(/filename="([^"]+)"/);
  const filename = utf8Match ? decodeURIComponent(utf8Match[1]) : (asciiMatch?.[1] ?? "export.xlsx");
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

// ponytail: 20 秒够所有普通读写；Agent 轮次单独放宽（见 AGENT_TURN_TIMEOUT_MS）。
const DEFAULT_TIMEOUT_MS = 20000;
// 后端 llm_timeout=30s 且会重试一次，加上工具与确定性核算，一轮合法耗时可能到 70s 左右。
const AGENT_TURN_TIMEOUT_MS = 120000;

// 同一个 GET 正在飞的时候又被发起，共享同一次请求，不重复打后端。
//
// 2026-08-20 实测：概览页点一只关注标的跳到行情页，一次跳转打了 21 个接口，
// 其中一半是 React.StrictMode 在开发模式下把 effect 跑两遍造成的（成对出现，
// 相隔 7ms）；深证成指的对比行情更是被要了三次，参数字节级相同、各 503KB，
// 合计 1.5MB。这些请求互相排队，把本来 76~240ms 的接口挤到 1.3~2.4 秒。
//
// 只合并"此刻同时在飞"的请求，请求一落地就从表里删掉——不是缓存，所以不存在
// 读到旧数据的问题。写操作（POST/PATCH/DELETE）一律不合并。
const inFlightGets = new Map();

function isDedupableGet(init) {
  const method = String(init.method || "GET").toUpperCase();
  return method === "GET" && !init.body;
}

// 合并之后多个调用方拿到的是同一份数据。谁就地改了它（排序、加字段）都会影响
// 别人，且极难排查，所以各给一份拷贝。相对一次网络往返，拷贝的开销可以忽略。
function detach(payload) {
  if (payload === null || typeof payload !== "object") return payload;
  if (typeof structuredClone === "function") return structuredClone(payload);
  return JSON.parse(JSON.stringify(payload));
}

async function fetchJson(path, options = {}) {
  const { timeoutMs = DEFAULT_TIMEOUT_MS, ...init } = options;
  if (!isDedupableGet(init)) return performJsonRequest(path, timeoutMs, init);

  const pending = inFlightGets.get(path);
  if (pending) return pending.then(detach);

  const request = performJsonRequest(path, timeoutMs, init);
  inFlightGets.set(path, request);
  request
    .catch(() => {})
    .finally(() => {
      if (inFlightGets.get(path) === request) inFlightGets.delete(path);
    });
  return request.then(detach);
}

async function performJsonRequest(path, timeoutMs, init) {
  let response;
  try {
    response = await fetch(`${API}${path}`, {
      credentials: "include",
      ...init,
      // 没有超时的话，后端"活着但不回话"时请求永远悬着：页面卡在
      // 「正在确认登录状态…」转圈到天荒地老，用户分不清是网慢、没登录还是服务挂了。
      // 2026-08-18 线上实测过一次，api 进程 online 但所有请求不返回（AGENT-022）。
      signal: AbortSignal.timeout(timeoutMs),
      headers: {
        ...(init.body ? { "Content-Type": "application/json" } : {}),
        ...(init.headers ?? {}),
      },
      body: init.body ? JSON.stringify(init.body) : undefined,
    });
  } catch (cause) {
    if (cause?.name === "TimeoutError" || cause?.name === "AbortError") {
      const error = new Error(
        `服务在 ${Math.round(timeoutMs / 1000)} 秒内没有响应，请稍后重试。`,
      );
      error.status = 0;
      error.timeout = true;
      throw error;
    }
    const error = new Error("无法连接服务，请检查网络后重试。");
    error.status = 0;
    throw error;
  }
  // FastAPI's default 500 response is plain text ("Internal Server Error").
  // Do not surface a JSON parser exception to a user when a downstream market
  // source is unavailable; preserve structured API errors when they exist.
  const raw = await response.text();
  let payload = null;
  if (raw) {
    try {
      payload = JSON.parse(raw);
    } catch {
      if (!response.ok) {
        const error = new Error(
          response.status >= 500
            ? "服务暂时不可用，请稍后重试。"
            : `请求失败（HTTP ${response.status}），请稍后重试。`,
        );
        error.status = response.status;
        throw error;
      }
      throw new Error("服务返回了无法识别的数据，请稍后重试。");
    }
  }
  if (!response.ok) {
    notifyAuthenticationRequired(path, response, payload);
    const error = new Error(
      payload?.error?.message
        ?? (response.status >= 500 ? "服务暂时不可用，请稍后重试。" : `HTTP ${response.status}`),
    );
    error.payload = payload;
    error.status = response.status;
    throw error;
  }
  return payload;
}

function notifyAuthenticationRequired(path, response, payload) {
  if (
    response.status === 401
    && payload?.error?.code === "authentication_required"
    && !path.startsWith("/api/auth/")
  ) {
    window.dispatchEvent(new CustomEvent("astock:authentication-required"));
  }
  // 订阅已到期/已取消（后端统一 403）：AuthBoundary 收到后整页换成停用页。
  // /api/subscription/* 本身后端是放行的，不会走到这里。
  const code = payload?.error?.code;
  if (
    response.status === 403
    && (code === "subscription_lapsed" || code === "subscription_canceled")
    && !path.startsWith("/api/subscription/")
  ) {
    window.dispatchEvent(
      new CustomEvent("astock:subscription-blocked", {
        detail: { code, details: payload.error.details ?? {} },
      }),
    );
  }
}
